import React from 'react';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Steps, Button, Form, Input, Select, DatePicker, TimePicker, message, Spin, Alert } from "antd";
import api from "../../api/axiosConfig";
import "./BookingModal.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const { Option } = Select;

const BookingModal = ({ open, onCancel, rehabCenter, user }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeLoading, setTimeLoading] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [bookingData, setBookingData] = useState({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    if (rehabCenter) {
      const serviceList = rehabCenter.services ? rehabCenter.services.split(',').map(s => s.trim()) : [];
      setServices(serviceList);
    }
    if (user) {
      form.setFieldsValue({
        name: user.name,
        age: user.age,
        gender: user.gender,
        contact_number: user.phoneNumber,
        email: user.email,
      });
    }
  }, [rehabCenter, user, form, open]);

  useEffect(() => {
    if (showSuccessAlert) {
      const timer = setTimeout(() => {
        navigate('/account');
        onCancel();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessAlert, navigate, onCancel]);

  const handleNext = async () => {
    try {
      const values = await form.validateFields();
      setBookingData({ ...bookingData, ...values });
      setCurrentStep(currentStep + 1);
    } catch (errorInfo) {
      console.log("Failed:", errorInfo);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleDateChange = async (date) => {
    form.setFieldsValue({ time: null }); // Reset time when date changes
    if (!date || !rehabCenter?.id) {
      setBookedTimes([]);
      return;
    }
    
    setTimeLoading(true);
    try {
      const formattedDate = date.format('YYYY-MM-DD');
      const response = await api.get(`/bookings/booked-slots/${rehabCenter.id}/${formattedDate}`);
      setBookedTimes(response.data);
    } catch (error) {
      message.error("Failed to load available time slots. Please try again.");
      setBookedTimes([]);
    } finally {
      setTimeLoading(false);
    }
  };

  const disabledTime = () => {
    if (!rehabCenter) return {};

    const openTime = rehabCenter.is_open_24_hours ? null : dayjs(rehabCenter.open_from, "HH:mm");
    const closeTime = rehabCenter.is_open_24_hours ? null : dayjs(rehabCenter.open_to, "HH:mm");
    
    const selectedDate = form.getFieldValue('date');
    const now = dayjs();

    const disabledHours = () => {
      let hours = [];
      if (openTime && closeTime) {
        for (let i = 0; i < 24; i++) {
          if (i < openTime.hour() || i > closeTime.hour()) {
            hours.push(i);
          }
        }
      }

      if (selectedDate && selectedDate.isSame(now, 'day')) {
        for (let i = 0; i < now.hour(); i++) {
          if (!hours.includes(i)) {
            hours.push(i);
          }
        }
      }
      return hours;
    };

    const disabledMinutes = (selectedHour) => {
      const disabled = new Set();

      // Disable minutes outside of working hours
      if (openTime && selectedHour === openTime.hour()) {
        for (let i = 0; i < openTime.minute(); i++) disabled.add(i);
      }
      if (closeTime && selectedHour === closeTime.hour()) {
        for (let i = closeTime.minute(); i < 60; i++) disabled.add(i);
      }

      // Disable booked minutes
      bookedTimes.forEach(bookedTime => {
        const time = dayjs(bookedTime, 'HH:mm:ss');
        if (time.hour() === selectedHour) {
          disabled.add(time.minute());
        }
      });
      
      // Disable past minutes if today
      if (selectedDate && selectedDate.isSame(now, 'day') && selectedHour === now.hour()) {
        for (let i = 0; i < now.minute(); i++) {
          disabled.add(i);
        }
      }

      return Array.from(disabled);
    };

    return {
      disabledHours,
      disabledMinutes,
    };
  };

  const handleBookingSubmit = async () => {
    if (!user) {
      message.error("Please log in to book an appointment.");
      onCancel();
      return;
    }

    try {
      const patientDetails = await form.validateFields();
      const combinedData = { ...bookingData, ...patientDetails };

      const finalPayload = {
        ...combinedData,
        rehabCenterId: rehabCenter.id,
        date: combinedData.date.format('YYYY-MM-DD'),
        time: combinedData.time.format('HH:mm:ss'),
      };

      setLoading(true);
      await api.post('/bookings', finalPayload);
      setLoading(false);
      setShowSuccessAlert(true);
    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.error || "Failed to book appointment. Please try again.";
      message.error(errorMessage);
      console.error("Booking failed:", error);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form.Item
            name="service"
            label="Service"
            rules={[{ required: true, message: "Please select a service" }]}
          >
            <Select placeholder="Choose a service">
              {services.map(service => (
                <Option key={service} value={service}>
                  {service}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      case 1:
        return (
          <>
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: "Please select a date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                onChange={handleDateChange}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
            <Form.Item
              name="time"
              label="Time"
              rules={[{ required: true, message: "Please select a time" }]}
            >
              <Spin spinning={timeLoading} size="small">
                <TimePicker
                  use12Hours
                  minuteStep={30}
                  format="h:mm a"
                  style={{ width: "100%" }}
                  disabledTime={disabledTime}
                  onChange={(time) => form.setFieldsValue({ time: time })}
                />
              </Spin>
            </Form.Item>
          </>
        );
      case 2:
        return (
          <>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter patient's name" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="age"
              label="Age"
              rules={[{ required: true, message: "Please enter patient's age" }]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[{ required: true, message: "Please select patient's gender" }]}
            >
              <Select>
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="contact_number"
              label="Contact Number"
              rules={[{ required: true, message: "Please enter contact number" ,len:10}]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'The input is not a valid E-mail!' }
              ]}
            >
              <Input />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  const steps = [
    { title: "Select Service" },
    { title: "Choose Date & Time" },
    { title: "Patient Details" },
  ];

  return (
    <Modal
      title={`Book Appointment at ${rehabCenter?.name}`}
      open={open}
      onCancel={() => {
        setCurrentStep(0);
        setBookedTimes([]);
        onCancel();
      }}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <Spin spinning={loading}>
        {showSuccessAlert && (
          <Alert
            message="Booking Successful"
            description="Your booking is complete! Redirecting to your account in 3 seconds..."
            type="success"
            showIcon
            closable
            onClose={() => setShowSuccessAlert(false)}
          />
        )}
        <Steps
          current={currentStep}
          className="booking-steps"
          items={steps.map(item => ({ key: item.title, title: item.title }))}
        />
        <Form form={form} layout="vertical" name="booking_form">
            <div className="steps-content">{renderStepContent()}</div>
        </Form>
        <div className="steps-action">
          {currentStep > 0 && (
            <Button style={{ margin: "0 8px" }} onClick={handlePrev}>
              Previous
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleBookingSubmit} loading={loading}>
              Book Appointment
            </Button>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default BookingModal;